
    import { Vue, Component, Prop, Watch } from 'vue-property-decorator'

    /**
     * LockoutOverlay - Display component for lockout/rate-limit countdown
     *
     * This is a "dumb" display component that shows the countdown value passed via props.
     * The parent component (via the mixin) manages the actual countdown interval.
     * This component just displays the value and emits 'expired' when it reaches 0.
     */
    @Component
    export default class LockoutOverlay extends Vue {
        @Prop({ type: String, required: true }) title!: string
        @Prop({ type: String, required: true }) description!: string
        @Prop({ type: String, required: true }) note!: string
        @Prop({ type: Number, required: true }) countdown!: number
        @Prop({ type: Number, required: true }) duration!: number

        // Track if we've already emitted expired to prevent duplicate emissions
        private hasEmittedExpired = false

        mounted() {
            // Check if already expired when mounted
            if (this.countdown <= 0 && !this.hasEmittedExpired) {
                this.hasEmittedExpired = true
                // Emit synchronously to ensure it fires before any unmounting
                this.$emit('expired')
            }
        }

        @Watch('countdown')
        onCountdownChange(newVal: number) {
            // Reset the flag if countdown goes back up (e.g., if component is reused)
            if (newVal > 0) {
                this.hasEmittedExpired = false
                return
            }

            // When countdown reaches 0, emit expired event immediately
            // Emit synchronously to ensure it fires before the component might be unmounted
            if (newVal <= 0 && !this.hasEmittedExpired) {
                this.hasEmittedExpired = true
                this.$emit('expired')
            }
        }

        get countdownFormatted(): string {
            // Ensure we never show negative values
            const safeCountdown = Math.max(0, this.countdown)
            const minutes = Math.floor(safeCountdown / 60)
            const seconds = safeCountdown % 60
            return `${minutes}:${seconds.toString().padStart(2, '0')}`
        }

        get progress(): number {
            if (this.duration === 0) return 0
            // Ensure we never show negative progress
            const safeCountdown = Math.max(0, this.countdown)
            return (safeCountdown / this.duration) * 100
        }
    }
